import * as v from 'valibot';

import { logEmptyResponseToSentry, logValidationErrorsToSentry } from '#shared';

import { EValidationIssueSeverity } from '#shared/api';
import type { DataWithValidation, ValidationIssue } from '#shared/api';

// ---------------------------------------------------------------------------
// FE types — mirror the BE contract for `GET /api/weather/snapshot` verbatim.
// (CommandCenterMap-BE: src/services/domainEvents/weather/{sigmetParse,
// metarParse,tafForecast,weatherSnapshotResponse}.ts — read directly to pin
// this down instead of guessing from the brief's shorthand.)
// ---------------------------------------------------------------------------

export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR';

export type SigmetSource = 'isigmet' | 'airsigmet';

export interface SigmetPoint {
  lon: number;
  lat: number;
}

export interface ActiveSigmet {
  /** Stable identity across fetches; the diff/dedup key. */
  key: string;
  source: SigmetSource;
  hazard: string;
  /** AIRSIGMET severity column (e.g. SEV); ISIGMET has none. */
  severity: string | null;
  validFrom: string | null;
  validTo: string | null;
  points: SigmetPoint[];
  base: number | null;
  top: number | null;
  movementDir: number | null;
  movementSpd: number | null;
  /** ISIGMET FIR name; null for AIRSIGMET. */
  firName: string | null;
  rawText: string;
}

export interface MetarSnapshotEntry {
  category: FlightCategory;
  /** ISO observation time as published by NOAA; null when unparseable. */
  obsTime: string | null;
  lat: number | null;
  lon: number | null;
  /** Raw METAR/SPECI bulletin text (FE tooltip source); '' when absent. */
  rawText: string;
}

export interface ForecastSegment {
  from: string;
  to: string;
  category: FlightCategory;
  ceilingFtAgl: number | null;
  visibilitySm: number | null;
}

export interface ForecastStation {
  raw: string;
  segments: ForecastSegment[];
}

/** REST response for `GET /api/weather/snapshot` (TMDXNC-13). */
export interface WeatherSnapshotResponse {
  sigmets: ActiveSigmet[];
  metars: Record<string, MetarSnapshotEntry>;
  forecasts: Record<string, ForecastStation>;
  sigmetSnapshotAt: string | null;
  metarSnapshotAt: string | null;
  tafSnapshotAt: string | null;
}

/** Sensible default when there's no data yet (loading) or the response was unusable. */
export const EMPTY_WEATHER_SNAPSHOT: WeatherSnapshotResponse = {
  sigmets: [],
  metars: {},
  forecasts: {},
  sigmetSnapshotAt: null,
  metarSnapshotAt: null,
  tafSnapshotAt: null,
};

// ---------------------------------------------------------------------------
// Canonical (strict) schema — validates the full contract shape. Used for
// direct round-trip checks; the RESILIENT read path is `validateWeatherSnapshotResponse`
// below, not a `v.safeParse` against this schema (see the landmine note there).
// ---------------------------------------------------------------------------

const FLIGHT_CATEGORIES = ['VFR', 'MVFR', 'IFR', 'LIFR'] as const;
const flightCategorySchema: v.GenericSchema<FlightCategory> =
  v.picklist(FLIGHT_CATEGORIES);

const sigmetPointSchema: v.GenericSchema<SigmetPoint> = v.object({
  lon: v.number(),
  lat: v.number(),
});

export const activeSigmetSchema: v.GenericSchema<ActiveSigmet> = v.object({
  key: v.string(),
  source: v.picklist(['isigmet', 'airsigmet']),
  hazard: v.string(),
  severity: v.nullable(v.string()),
  validFrom: v.nullable(
    v.pipe(
      v.string(),
      v.isoTimestamp('@WARNING: SIGMET validFrom is not a valid ISO timestamp'),
    ),
  ),
  validTo: v.nullable(
    v.pipe(
      v.string(),
      v.isoTimestamp('@WARNING: SIGMET validTo is not a valid ISO timestamp'),
    ),
  ),
  points: v.array(sigmetPointSchema),
  base: v.nullable(v.number()),
  top: v.nullable(v.number()),
  movementDir: v.nullable(v.number()),
  movementSpd: v.nullable(v.number()),
  firName: v.nullable(v.string()),
  rawText: v.string(),
});

const metarEntrySchema: v.GenericSchema<MetarSnapshotEntry> = v.object({
  category: flightCategorySchema,
  obsTime: v.nullable(
    v.pipe(
      v.string(),
      v.isoTimestamp('@WARNING: METAR obsTime is not a valid ISO timestamp'),
    ),
  ),
  lat: v.nullable(v.number()),
  lon: v.nullable(v.number()),
  rawText: v.string(),
});

const forecastSegmentSchema: v.GenericSchema<ForecastSegment> = v.object({
  from: v.pipe(
    v.string(),
    v.isoTimestamp(
      '@WARNING: forecast segment "from" is not a valid ISO timestamp',
    ),
  ),
  to: v.pipe(
    v.string(),
    v.isoTimestamp(
      '@WARNING: forecast segment "to" is not a valid ISO timestamp',
    ),
  ),
  category: flightCategorySchema,
  ceilingFtAgl: v.nullable(v.number()),
  visibilitySm: v.nullable(v.number()),
});

const forecastStationSchema: v.GenericSchema<ForecastStation> = v.object({
  raw: v.string(),
  segments: v.array(forecastSegmentSchema),
});

export const weatherSnapshotResponseSchema: v.GenericSchema<WeatherSnapshotResponse> =
  v.object({
    sigmets: v.array(activeSigmetSchema),
    metars: v.record(v.string(), metarEntrySchema),
    forecasts: v.record(v.string(), forecastStationSchema),
    sigmetSnapshotAt: v.nullable(v.string()),
    metarSnapshotAt: v.nullable(v.string()),
    tafSnapshotAt: v.nullable(v.string()),
  });

// ---------------------------------------------------------------------------
// Resilient read path.
//
// LANDMINE: `validateAndTransformResponse` (#shared/api) and `fallbackWithIssue`
// (entities/schemas/valibot.helpers) are built for a top-level ARRAY of items
// under a `{data, dataGeneratedAt, errors, expires, errorsGeneratedAt}`
// envelope, dropping ERROR-graded items by array index. This endpoint returns
// a bare top-level OBJECT (verified against the NestJS controller —
// `WeatherSnapshotController#getWeatherSnapshot` returns the DTO directly, no
// envelope) with NESTED collections (`sigmets[]`, `metars{}`, `forecasts{}`),
// so neither the envelope shape nor the array-index drop logic applies.
//
// Separately — and this is worth flagging for whoever owns `valibot.helpers.ts`
// — `fallbackWithIssue` does not actually substitute its fallback value.
// `v.rawCheck`'s `~run` (valibot@1.2.x, checked against node_modules/valibot/dist/index.cjs)
// unconditionally `return`s the ORIGINAL dataset it was given, discarding
// whatever the action callback returns:
//   "~run"(dataset, config) { action({dataset, config, addIssue}); return dataset; }
// so `fallbackWithIssue`'s `return { typed: true, value: fallback }` is dead
// code (confirmed empirically with a throwaway v.safeParse against the actual
// helper). It only relabels an issue's severity (WARNING vs the default
// ERROR); the invalid raw value is left in place. That's consistent with how
// `activeCasesResponseSchema`'s own test exercises it (only asserts the
// warning MESSAGE appears — the `expectedFallback` value in that test's cases
// is never actually asserted). It's harmless there because
// `validateAndTransformResponse` keeps/drops whole ARRAY ITEMS by severity
// and never reads the fallback value either. It would NOT be harmless here,
// where a concrete substituted value (e.g. metar `rawText` becoming `''`) is
// required and tested. `gradeItem` below is a small local replacement that
// actually swaps in the fallback per field.
// ---------------------------------------------------------------------------

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const FALLBACK_SIGMET: ActiveSigmet = {
  key: 'unknown-sigmet',
  source: 'isigmet',
  hazard: 'UNKNOWN',
  severity: null,
  validFrom: null,
  validTo: null,
  points: [],
  base: null,
  top: null,
  movementDir: null,
  movementSpd: null,
  firName: null,
  rawText: '',
};

const FALLBACK_METAR_ENTRY: MetarSnapshotEntry = {
  // Conservative default: an unreadable category defaults to the WORST flight
  // category (LIFR) rather than the best (VFR) — under-reporting a hazard is
  // worse than over-reporting one.
  category: 'LIFR',
  obsTime: null,
  lat: null,
  lon: null,
  rawText: '',
};

const FALLBACK_FORECAST_SEGMENT: ForecastSegment = {
  from: '',
  to: '',
  // Conservative default: an unreadable category defaults to the WORST flight
  // category (LIFR) rather than the best (VFR) — under-reporting a hazard is
  // worse than over-reporting one (same rationale as FALLBACK_METAR_ENTRY above).
  category: 'LIFR',
  ceilingFtAgl: null,
  visibilitySm: null,
};

interface GradedItem<T> {
  value: T;
  failedFields: string[];
}

/**
 * Validates `rawItem` against `itemSchema`. Any field that individually fails
 * is replaced by the matching field from `fallback`; every field that DID
 * validate is kept as-is (DATA-001: a malformed nested item is kept with a
 * per-field fallback, never dropped). If `rawItem` isn't even a plain object,
 * the whole `fallback` is used.
 */
const gradeItem = <T extends object>(
  itemSchema: v.GenericSchema<T>,
  rawItem: unknown,
  fallback: T,
): GradedItem<T> => {
  const result = v.safeParse(itemSchema, rawItem);
  if (result.success) {
    return { value: result.output, failedFields: [] };
  }
  if (!isPlainObject(rawItem)) {
    return { value: fallback, failedFields: ['(entire item)'] };
  }

  const failedFields = [
    ...new Set(
      result.issues
        .map(issue => issue.path?.[0]?.key)
        .filter((key): key is string => typeof key === 'string'),
    ),
  ];
  const fallbackRecord = fallback as Record<string, unknown>;
  const outputRecord = result.output as Record<string, unknown>;
  const sanitized: Record<string, unknown> = {
    ...fallbackRecord,
    ...outputRecord,
  };
  for (const key of failedFields) {
    sanitized[key] = fallbackRecord[key];
  }
  // Sound: `sanitized` starts from `fallbackRecord` (which is a valid `T` by
  // construction — every FALLBACK_* constant above is typed `T`) and is only
  // overlaid with `outputRecord`'s fields, which valibot still populates for
  // a dataset even when some fields individually fail (`~run` keeps parsing
  // siblings rather than bailing out) — so every key of `T` is present with
  // either a validated value or its fallback.
  return { value: sanitized as T, failedFields };
};

/**
 * `severity` defaults to WARNING (kept-with-fallback nested-item issues).
 * DATA-001: pass ERROR for STRUCTURAL breaks — the response isn't a plain
 * object, or a top-level collection (`sigmets`/`metars`/`forecasts`) is
 * missing or the wrong type — which must escalate to Sentry, not just track
 * silently. See `validateWeatherSnapshotResponse`'s Sentry calls below.
 */
const makeIssue = (
  path: (string | number)[],
  message: string,
  severity: EValidationIssueSeverity = EValidationIssueSeverity.WARNING,
): ValidationIssue => {
  const pathKey = path.join('.') || 'root';
  return {
    path,
    pathKey,
    severity,
    message,
    key: pathKey,
  };
};

/** Shape-only: `segments` is graded per-element below, not by the entry schema. */
interface ForecastEntryShape {
  raw: string;
  segments: unknown[];
}

const forecastEntryShapeSchema: v.GenericSchema<ForecastEntryShape> = v.object({
  raw: v.string(),
  segments: v.array(v.unknown()),
});

const gradeForecastStation = (
  rawEntry: unknown,
  icao: string,
): { value: ForecastStation; issues: ValidationIssue[] } => {
  const issues: ValidationIssue[] = [];
  const { value: shape, failedFields } = gradeItem(
    forecastEntryShapeSchema,
    rawEntry,
    { raw: '', segments: [] },
  );
  if (failedFields.length > 0) {
    issues.push(
      makeIssue(
        ['forecasts', icao],
        `Malformed forecast entry for ${icao} — defaulted: ${failedFields.join(', ')}`,
      ),
    );
  }

  const segments = shape.segments.map((rawSegment, index) => {
    const graded = gradeItem(
      forecastSegmentSchema,
      rawSegment,
      FALLBACK_FORECAST_SEGMENT,
    );
    if (graded.failedFields.length > 0) {
      issues.push(
        makeIssue(
          ['forecasts', icao, 'segments', index],
          `Malformed forecast segment — defaulted: ${graded.failedFields.join(', ')}`,
        ),
      );
    }
    return graded.value;
  });

  return { value: { raw: shape.raw, segments }, issues };
};

/**
 * Validates + sanitizes a `GET /api/weather/snapshot` response. Grades every
 * nested item (sigmet / metar entry / forecast entry / forecast segment)
 * with a per-field fallback rather than dropping it — an empty `sigmets[]`,
 * `metars{}`, or `forecasts{}` is a legitimate (not erroneous) snapshot.
 */
export const validateWeatherSnapshotResponse = (
  response: unknown,
): DataWithValidation<WeatherSnapshotResponse> => {
  if (!isPlainObject(response)) {
    // Total structural break — the whole response is unusable. ERROR (not
    // WARNING): DATA-001 requires this escalate to Sentry, and it is also
    // effectively an "empty response" (we serve EMPTY_WEATHER_SNAPSHOT), so
    // both dedicated Sentry signals fire — mirrors the standard
    // `validateAndTransformResponse` path's two-signal telemetry.
    const issue = makeIssue(
      ['root'],
      'Weather snapshot response is not an object — using an empty snapshot',
      EValidationIssueSeverity.ERROR,
    );
    logValidationErrorsToSentry('weatherSnapshot', [issue], 1, 1);
    logEmptyResponseToSentry('weatherSnapshot');
    return {
      data: EMPTY_WEATHER_SNAPSHOT,
      generatedAt: null,
      validation: {
        isValid: false,
        issues: [issue],
      },
    };
  }

  const issues: ValidationIssue[] = [];

  const rawSigmets = response.sigmets;
  if (!Array.isArray(rawSigmets)) {
    issues.push(
      makeIssue(
        ['sigmets'],
        'sigmets missing or malformed — defaulted to []',
        EValidationIssueSeverity.ERROR,
      ),
    );
  }
  const sigmets = (Array.isArray(rawSigmets) ? rawSigmets : []).map(
    (rawSigmet, index) => {
      const graded = gradeItem(activeSigmetSchema, rawSigmet, FALLBACK_SIGMET);
      if (graded.failedFields.length > 0) {
        issues.push(
          makeIssue(
            ['sigmets', index],
            `Malformed SIGMET entry — defaulted: ${graded.failedFields.join(', ')}`,
          ),
        );
      }
      return graded.value;
    },
  );

  const rawMetars = response.metars;
  if (!isPlainObject(rawMetars)) {
    issues.push(
      makeIssue(
        ['metars'],
        'metars missing or malformed — defaulted to {}',
        EValidationIssueSeverity.ERROR,
      ),
    );
  }
  const metars: Record<string, MetarSnapshotEntry> = {};
  for (const [icao, rawEntry] of Object.entries(
    isPlainObject(rawMetars) ? rawMetars : {},
  )) {
    const graded = gradeItem(metarEntrySchema, rawEntry, FALLBACK_METAR_ENTRY);
    if (graded.failedFields.length > 0) {
      issues.push(
        makeIssue(
          ['metars', icao],
          `Malformed METAR entry for ${icao} — defaulted: ${graded.failedFields.join(', ')}`,
        ),
      );
    }
    metars[icao] = graded.value;
  }

  const rawForecasts = response.forecasts;
  if (!isPlainObject(rawForecasts)) {
    issues.push(
      makeIssue(
        ['forecasts'],
        'forecasts missing or malformed — defaulted to {}',
        EValidationIssueSeverity.ERROR,
      ),
    );
  }
  const forecasts: Record<string, ForecastStation> = {};
  for (const [icao, rawEntry] of Object.entries(
    isPlainObject(rawForecasts) ? rawForecasts : {},
  )) {
    const graded = gradeForecastStation(rawEntry, icao);
    issues.push(...graded.issues);
    forecasts[icao] = graded.value;
  }

  const gradeSnapshotAt = (field: string, raw: unknown): string | null => {
    if (raw === null || raw === undefined) {
      return null;
    }
    if (typeof raw === 'string') {
      return raw;
    }
    issues.push(
      makeIssue(
        [field],
        `${field} is not a string or null — defaulted to null`,
      ),
    );
    return null;
  };

  // DATA-001: escalate STRUCTURAL breaks (sigmets/metars/forecasts missing or
  // the wrong top-level type) to Sentry. Per-field/per-item WARNING issues
  // (bad nested category, missing rawText, bad point, …) are kept-with-
  // fallback by design and must NOT trigger this.
  const structuralErrorIssues = issues.filter(
    issue => issue.severity === EValidationIssueSeverity.ERROR,
  );
  if (structuralErrorIssues.length > 0) {
    // "totalItems"/"filteredCount" don't map to array items here — the three
    // top-level collections stand in for them (N of 3 structurally broken).
    logValidationErrorsToSentry(
      'weatherSnapshot',
      structuralErrorIssues,
      3,
      structuralErrorIssues.length,
    );
  }

  return {
    data: {
      sigmets,
      metars,
      forecasts,
      sigmetSnapshotAt: gradeSnapshotAt(
        'sigmetSnapshotAt',
        response.sigmetSnapshotAt,
      ),
      metarSnapshotAt: gradeSnapshotAt(
        'metarSnapshotAt',
        response.metarSnapshotAt,
      ),
      tafSnapshotAt: gradeSnapshotAt('tafSnapshotAt', response.tafSnapshotAt),
    },
    generatedAt: null,
    validation: {
      isValid: issues.length === 0,
      issues,
    },
  };
};
